import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {UseFormSetValue} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {
    adjustLastMarker,
    DURATION_MARKERS,
    DURATION_MAX_VALUE,
    DURATION_MIN_VALUE,
    getDurationByOffset,
    getDurationFillWidthPct,
    getDurationProgressPct,
    splitDuration,
} from 'features/http/lib/durationSliderView';
import useBreakpoint from 'shared/lib/hooks/useBreakpoint';
import Slider from 'shared/ui/Slider/Slider';

interface IDurationSliderProps {
    name: string;
    setValue: UseFormSetValue<any>;
    defaultValue?: number;
    disabled?: boolean;
    onChange?: (_value: number) => void;
}

const formatDuration = (seconds: number): string => {
    const {unit, amount} = splitDuration(seconds);

    switch (unit) {
        case 'seconds':
            return `${amount} ${t`с`}`;
        case 'minutes':
            return `${amount} ${t`м`}`;
        default:
            return `${amount} ${t`ч`}`;
    }
};

const markerLabel = (value: number): string => {
    switch (value) {
        case 1:
            return `1 ${t`с`}`;
        case 30:
            return `30 ${t`с`}`;
        case 60:
            return `1 ${t`м`}`;
        case 1800:
            return `30 ${t`м`}`;
        case 3600:
            return `1 ${t`ч`}`;
        case 18000:
            return `5 ${t`ч`}`;
        case 36000:
            return `10 ${t`ч`}`;
        case 54000:
            return `15 ${t`ч`}`;
        default:
            return `24 ${t`ч`}`;
    }
};

const DurationSlider: React.FC<IDurationSliderProps> = ({
    name,
    defaultValue = 3600,
    setValue,
    disabled = false,
    onChange,
}) => {
    const [secondsValue, setSecondsValue] = useState(defaultValue);
    const breakpoint = useBreakpoint();

    useEffect(() => {
        setSecondsValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
        setValue(name, secondsValue, {shouldDirty: true});
        onChange?.(secondsValue);
    }, [secondsValue, name, setValue, onChange]);

    const markers = useMemo(
        () => adjustLastMarker(DURATION_MARKERS, breakpoint === 'mini').map((marker) => ({
            key: marker.value,
            position: marker.position,
            label: markerLabel(marker.value),
            overlapped: marker.value <= secondsValue,
        })),
        [breakpoint, secondsValue],
    );

    const progressPct = getDurationProgressPct(secondsValue);

    const onOffset = useCallback((offset: number, width: number) => {
        setSecondsValue(getDurationByOffset(offset, width));
    }, []);

    return (
        <Slider
            name={name}
            value={secondsValue}
            min={DURATION_MIN_VALUE}
            max={DURATION_MAX_VALUE}
            progressPct={progressPct}
            fillPct={getDurationFillWidthPct(progressPct, secondsValue)}
            markers={markers}
            thumbLabel={formatDuration(secondsValue)}
            disabled={disabled}
            onOffset={onOffset}
        />
    );
};

export default DurationSlider;
